import { IFiltersPagination, IResponse } from "@app/common/types/http";
import { HTTP_ADMIN, HTTP_ADMISSION, HTTP_DISPENSARY, HTTP_PAYROLL } from "@app/config/axios";
import {
  bankEntitiesResponse,
  DeliveryPayload,
  InventoryStockConfirmPayload,
  InventoryStockConfirmResponse,
  InventoryStockFilters,
  InventoryStockResponse,
  OrderListFilters,
  OrderListPatient,
  OrderListResponse,
  paymentListResponse,
  PricePatientPayload,
  reminderDispensationPayload,
  TopInventoryArticle,
  TopInventoryFilters,
  WalletBalanceFilters
} from "./types";

export async function listOrdersService(
  filters: OrderListFilters & IFiltersPagination,
  token: string
) {
  const res = await HTTP_DISPENSARY(token).get<IResponse<OrderListPatient[]>>("/ordering/", {
    params: filters
  });
  return res.data;
}

export async function orderDetail(filters: { orderId?: number; disId?: number }, token: string) {
  const res = await HTTP_DISPENSARY(token).get<IResponse<OrderListResponse>>("/dispensaries/", {
    params: filters
  });
  return res.data;
}

export async function paymentListService(filters: { eaccount: number }, token: string) {
  const res = await HTTP_ADMISSION(token).get<IResponse<paymentListResponse[]>>(
    "/admissions/depositType/",
    {
      params: filters
    }
  );
  return res.data;
}
export async function bankListService(filters: { eaccount: number }, token: string) {
  const res = await HTTP_ADMIN(token).get<IResponse<bankEntitiesResponse[]>>(
    "/admissions/bankingEntities",
    {
      params: filters
    }
  );
  return res.data;
}
export async function documentListService(token: string) {
  const res = await HTTP_PAYROLL(token).get<
    IResponse<{ id: number; description: string; prefix: string }[]>
  >("/payroll/document_types/", {
    params: {}
  });
  return res.data;
}

export async function pricePatientService(payload: PricePatientPayload, token: string) {
  const axiosInstance = HTTP_DISPENSARY(token);
  const response = await axiosInstance.post<IResponse>("/dispensaries/dispensary-price", payload);
  return response.data;
}
export async function deliveryDispenseService(payload: DeliveryPayload, token: string) {
  const axiosInstance = HTTP_DISPENSARY(token);
  const sanitizedPayload: DeliveryPayload = {
    ...payload,
    medicines: payload.medicines?.map((medicine) => ({
      id: medicine.id,
      articleId: medicine.articleId,
      batchId: medicine.batchId,
      qtyDelivered: medicine.qtyDelivered,
      qtyDispensed: medicine.qtyDispensed,
      qtyOrder: medicine.qtyOrder,
      lot: medicine.lot,
      presentationId: medicine.presentationId,
      patientPrice: medicine.patientPrice,
      companyPrice: medicine.companyPrice,
      warehouseCode: medicine.warehouseCode,
      warehouseName: medicine.warehouseName,
      description: medicine.description,
      name: medicine.name
    }))
  };
  const response = await axiosInstance.post<IResponse>("/dispensaries/", sanitizedPayload);
  return response.data;
}

export async function deliveryPdfDetail(filters: { disId: number }, token: string) {
  const res = await HTTP_DISPENSARY(token).get<IResponse<{ base64: string }>>("/dispensaries/pdf", {
    params: filters
  });
  return res.data;
}

export async function deliveryReminderService(payload: reminderDispensationPayload, token: string) {
  const axiosInstance = HTTP_DISPENSARY(token);
  const response = await axiosInstance.post<IResponse>(
    `/dispensaries/${payload.id}/reminder`,
    payload
  );
  return response.data;
}

export async function updateMedicineLotService(
  dispensaryId: number,
  medicineId: number,
  lot: string,
  token: string
) {
  const axiosInstance = HTTP_DISPENSARY(token);
  const response = await axiosInstance.patch<IResponse>(
    `/dispensaries/${dispensaryId}/medicines/${medicineId}`,
    { lot }
  );
  return response.data;
}

export async function inventoryStockService(
  filters: InventoryStockFilters,
  token: string
): Promise<InventoryStockResponse> {
  const res = await HTTP_DISPENSARY(token).get<InventoryStockResponse>("/lots/inventory-stock", {
    params: filters
  });
  return res.data;
}

export async function topInventoryService(filters: TopInventoryFilters, token: string) {
  const res = await HTTP_DISPENSARY(token).get<IResponse<TopInventoryArticle[]>>(
    "/lots/top-inventory",
    { params: filters }
  );
  return res.data;
}

export async function confirmInventoryStock(
  payload: InventoryStockConfirmPayload,
  token: string
): Promise<IResponse<InventoryStockConfirmResponse>> {
  const response = await HTTP_DISPENSARY(token).post<IResponse<InventoryStockConfirmResponse>>(
    "/lots/inventory-stock/confirm",
    payload
  );
  return response.data;
}

export async function walletBalanceService(filters: WalletBalanceFilters, token: string) {
  const { patientId, isExternalPatient } = filters;
  const res = await HTTP_ADMISSION(token).get<IResponse<{ totalPendingAmount: number }>>(
    `/patient/${patientId}/wallet/balance`,
    {
      params:
        isExternalPatient === undefined ? undefined : { is_external_patient: isExternalPatient }
    }
  );
  return res.data;
}
